import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Investor, Contact } from "@/types/investor";
import { loadFromSupabaseThenLocal } from "@/utils/investors/databaseUtils";
import { useInvestorData } from "./useInvestorData";
import { useInvestorSync } from "./useInvestorSync";
import { useInvestorActions } from "./useInvestorActions";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";
import { supabase } from "@/integrations/supabase/client";
import { hookDebugUtils } from "@/utils/ai/debugUtils";
import { markStateFresh, checkSessionActivity, checkForStaleState } from "@/utils/investors/processing/processingState";

export const useInvestorProcessing = () => {
  // Add hook instance ID for debugging
  const instanceId = useRef(`hook_${Math.random().toString(36).substr(2, 9)}`);
  const hookMountTime = useRef(new Date().toISOString());
  
  // Register hook instance for debugging
  useEffect(() => {
    hookDebugUtils.registerHook('useInvestorProcessing', instanceId.current);
    
    console.log(`useInvestorProcessing ${instanceId.current} initialized at ${hookMountTime.current}`);
    
    return () => {
      hookDebugUtils.unregisterHook('useInvestorProcessing');
      console.log(`useInvestorProcessing ${instanceId.current} unmounted after ${((new Date().getTime() - new Date(hookMountTime.current).getTime()) / 1000).toFixed(2)}s`);
    };
  }, []);
  
  // Get auth status - removed loading context dependency
  const { isAuthenticated, supabaseSession } = useGoogleAuth();
  
  // Data management hook
  const {
    investors, setInvestors,
    contacts, setContacts,
    processedIds, setProcessedIds,
    selectedInvestor, setSelectedInvestor,
    businessInfo, setBusinessInfo, 
    relevanceContext, setRelevanceContext,
    isLoadingData, setIsLoadingData,
    initialDataLoaded, isSaving, isSyncing
  } = useInvestorData();
  
  // Add logging for investors array changes
  useEffect(() => {
    console.log(`useInvestorProcessing ${instanceId.current} - investors array updated, length: ${investors.length}`);
  }, [investors]);
  
  // Sync hook - pass the setter functions to allow periodic updates
  const { supabaseDataLoaded, setSyncDisabled } = useInvestorSync(
    initialDataLoaded.current,
    investors,
    contacts,
    processedIds,
    relevanceContext,
    businessInfo,
    isSaving,
    isSyncing,
    setInvestors,
    setContacts,
    setBusinessInfo,
    setRelevanceContext
  );
  
  // Actions hook
  const {
    isAnalyzing,
    isProcessingBatch,
    isEnriching,
    setIsEnriching,
    handleFileData,
    handleRelevanceSubmit,
    handleProcessInvestor,
    handleEnrichInvestor,
    handleProcessBatch,
    handleReprocess,
    handleInvestorUpdate
  } = useInvestorActions(
    investors,
    setInvestors,
    contacts,
    setContacts,
    selectedInvestor,
    setSelectedInvestor,
    processedIds,
    setProcessedIds,
    relevanceContext
  );
  
  // Check for stale state when app regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log("Application regained focus, checking for stale state");
        
        // Check for session inactivity
        const wasInactive = checkSessionActivity();
        
        // If inactive for a while and we have data loaded, check if state might be stale
        if (wasInactive && investors.length > 0 && initialDataLoaded.current) {
          const stateIsPotentiallyStale = checkForStaleState();
          
          if (stateIsPotentiallyStale) {
            console.log("Detected potentially stale state after period of inactivity");
            toast.warning("Session was inactive for a while. Consider reloading data to ensure freshness.");
          }
        }
      }
    };
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for focus events
    window.addEventListener('focus', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [investors.length, initialDataLoaded]);
  
  // Updated failsafe function without page loading
  const forceReloadFromDatabase = useCallback(async () => {
    if (!isAuthenticated || !supabaseSession?.user?.id) {
      console.warn(`forceReloadFromDatabase: Not authenticated, cannot reload`);
      toast.error("Cannot reload data - you are not logged in");
      return false;
    }
    
    try {
      console.log(`forceReloadFromDatabase: Starting reload for user ${supabaseSession.user.id}`);
      setIsLoadingData(true);
      
      // Direct database query to bypass caching
      const { data: investorData, error } = await supabase
        .from('investor_data')
        .select('*')
        .eq('user_id', supabaseSession.user.id)
        .single();
        
      if (error) {
        console.error("Database query error:", error);
        toast.error("Failed to load data from database");
        return false;
      }
      
      if (!investorData) {
        console.warn("No investor data found in database");
        toast.warning("No investor data found in database");
        return false;
      }
        
      // Fix for the TypeScript error - Check if investors is an array before accessing length
      const investorsJson = investorData?.investors;
      const investorsCount = Array.isArray(investorsJson) ? investorsJson.length : 0;
      console.log(`Database query returned data with ${investorsCount} investors`);
      
      const loadedInvestors = investorData.investors as unknown as Investor[];
      const loadedContacts = investorData.contacts as unknown as Contact[];
      
      if (Array.isArray(loadedInvestors) && loadedInvestors.length > 0) {
        console.log(`Setting ${loadedInvestors.length} investors from database`);
        setInvestors(loadedInvestors);
        
        const newProcessedIds = new Set<string>();
        let processedCount = 0;
        loadedInvestors.forEach(inv => {
          if (inv.enrichedWith) {
            newProcessedIds.add(inv.id);
            processedCount++;
          }
        });
        console.log(`Found ${processedCount} processed investors`);
        setProcessedIds(newProcessedIds);
        
        markStateFresh();
        
        toast.success(`Loaded ${loadedInvestors.length} investors from database`);
        return true;
      } else {
        console.warn("No investors in the returned data");
        toast.warning("No investor data found in database");
        return false;
      }
    } catch (error) {
      console.error("Error in forceReloadFromDatabase:", error);
      toast.error("Error reloading data from database");
      return false;
    } finally {
      setIsLoadingData(false);
    }
  }, [isAuthenticated, supabaseSession, setInvestors, setProcessedIds, setIsLoadingData]);
  
  // Effect to monitor authentication state and load data when logging in
  useEffect(() => {
    if (isAuthenticated && supabaseSession) {
      console.log(`useInvestorProcessing ${instanceId.current} - User authenticated, checking for Supabase data`);
      loadSavedData();
    }
  }, [isAuthenticated, supabaseSession]);
  
  // Effect to force reload data when navigating back to the page
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      // If page is being shown (not hidden) after navigation and we have initial data loaded
      if (!e.persisted && initialDataLoaded.current) {
        console.log(`useInvestorProcessing ${instanceId.current} - Page shown after navigation, reloading data`);
        // Force a data reload to ensure we have the latest data
        loadSavedData();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);
  
  // Updated loadSavedData without page loading
  const loadSavedData = useCallback(async () => {
    if (isLoadingData) {
      console.log(`loadSavedData: Already loading data, skipping`);
      return { investors, contacts };
    }
    
    try {
      const startTime = Date.now();
      setIsLoadingData(true);
      console.log(`loadSavedData: Starting load at ${new Date().toISOString()}`);
      
      const userId = supabaseSession?.user?.id;
      
      if (userId) {
        console.log(`loadSavedData: Loading data for authenticated user ${userId}`);
        const { investors: loadedInvestors, contacts: loadedContacts, processedIds: loadedIds } = 
          await loadFromSupabaseThenLocal(userId);
        
        if (loadedInvestors && Array.isArray(loadedInvestors) && loadedInvestors.length > 0) {
          console.log(`loadSavedData: Loaded ${loadedInvestors.length} investors from database`);
          setInvestors(loadedInvestors);
        } else {
          console.log("loadSavedData: No investors found in database, keeping current state");
        }
        
        if (loadedContacts && Array.isArray(loadedContacts) && loadedContacts.length > 0) {
          console.log(`loadSavedData: Loaded ${loadedContacts.length} contacts from database`);
          setContacts(loadedContacts);
        } else {
          console.log("loadSavedData: No contacts found in database, keeping current state");
        }
        
        if (loadedIds && loadedIds.size > 0) {
          console.log(`loadSavedData: Setting ${loadedIds.size} processedIds`);
          setProcessedIds(loadedIds);
        }
        
        // Load context data from database
        try {
          const { data: contextData } = await supabase
            .from('ai_context')
            .select('business_info, relevance_context')
            .eq('user_id', userId)
            .maybeSingle();
            
          if (contextData) {
            if (contextData.business_info) {
              setBusinessInfo(contextData.business_info);
              console.log("loadSavedData: Loaded business info from database");
            }
            
            if (contextData.relevance_context) {
              setRelevanceContext(contextData.relevance_context);
              console.log("loadSavedData: Loaded relevance context from database");
            }
          }
        } catch (contextError) {
          console.error("Error loading context data:", contextError);
        }
        
        initialDataLoaded.current = true;
        markStateFresh();
        
        console.log(`loadSavedData complete in ${Date.now() - startTime}ms - Data loaded from database`);
        hookDebugUtils.logPerformance("Data loading", startTime);
        
        return { 
          investors: loadedInvestors || [], 
          contacts: loadedContacts || [] 
        };
      } else {
        console.log("loadSavedData: Not authenticated");
        initialDataLoaded.current = true;
        console.log(`loadSavedData complete in ${Date.now() - startTime}ms - No authentication`);
        
        return { investors: [], contacts: [] };
      }
    } catch (error) {
      console.error("Error in loadSavedData:", error);
      return { investors: [], contacts: [] };
    } finally {
      setIsLoadingData(false);
    }
  }, [
    isLoadingData, 
    supabaseSession,
    setIsLoadingData,
    setInvestors, 
    setContacts, 
    setProcessedIds, 
    setBusinessInfo, 
    setRelevanceContext, 
    investors, 
    contacts, 
    initialDataLoaded
  ]);
  
  // Helper function to check if an investor has been processed
  const isInvestorProcessed = (investor: Investor): boolean => {
    return !!investor.enrichedWith;
  };

  return {
    // Data state
    investors,
    contacts,
    businessInfo,
    relevanceContext,
    selectedInvestor,
    processedIds,
    
    // Loading states - added isLoadingData for components to use
    isLoadingData,
    
    // State setters
    setBusinessInfo,
    setRelevanceContext,
    setSelectedInvestor,
    setIsEnriching,
    
    // Processing states
    isAnalyzing,
    isProcessingBatch,
    isEnriching,
    
    // Methods
    loadSavedData,
    forceReloadFromDatabase,
    handleFileData,
    handleRelevanceSubmit,
    handleProcessInvestor,
    handleEnrichInvestor,
    handleProcessBatch,
    handleReprocess,
    handleInvestorUpdate,
    setSyncDisabled,
    
    // Helper functions
    isInvestorProcessed,
    
    // Debug info
    _debug: {
      instanceId: instanceId.current,
      mountTime: hookMountTime.current
    }
  };
};

export { useInvestorProcessing as default };
